import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Icon,
  Divider,
  Stack,
  Chip,
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import {
  KitchenRecipe,
  IngredientCost,
  RecipeIngredient,
  APIInstruction,
} from "./types";
import {
  getUnitDefinition,
  getUnitCategory,
  getCommonUnits,
  convertUnits,
  formatUnitValue,
  suggestAlternativeUnit,
} from "./utils/units";

interface RecipeDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  recipe: KitchenRecipe | null;
}

export const RecipeDetailsDrawer: React.FC<RecipeDetailsDrawerProps> = ({
  open,
  onClose,
  recipe,
}) => {
  const theme = useTheme();

  if (!recipe) return null;

  // Calculate total cost from ingredients
  const totalCost = recipe.total_cost;
  const latestCostHistory = recipe.cost_history?.[0];

  // Helper function to get ingredient details from recipe ingredients
  const getIngredientDetails = (itemId: string) => {
    const ingredient = recipe.ingredients.find((ing) => ing.item_id === itemId);
    return {
      name: ingredient?.brand_name || "Unknown Ingredient",
      parentUnit: ingredient?.parent_unit,
    };
  };

  // Helper function to get standardized unit and rate
  const getStandardizedRate = (cost: IngredientCost, parentUnit?: string) => {
    const unitDef = getUnitDefinition(cost.unit);
    if (!unitDef)
      return { standardRate: cost.cost_per_unit, standardUnit: cost.unit };

    // If parent unit exists, convert to that unit
    if (parentUnit) {
      const convertedRate = convertUnits(
        cost.cost_per_unit,
        cost.unit,
        parentUnit
      );
      if (convertedRate !== null) {
        return {
          standardRate: convertedRate,
          standardUnit: parentUnit,
        };
      }
    }

    // Fallback to largest common unit if parent unit conversion fails
    const commonUnits = getCommonUnits(unitDef.category);
    const largestUnit = commonUnits[commonUnits.length - 1];
    const convertedRate = convertUnits(
      cost.cost_per_unit,
      cost.unit,
      largestUnit.symbol
    );

    return {
      standardRate: convertedRate || cost.cost_per_unit,
      standardUnit: largestUnit.symbol,
    };
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 600,
          bgcolor: "background.default",
        },
      }}
    >
      <Box
        component={motion.div}
        initial={{ x: 20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        sx={{ height: "100%" }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <IconButton onClick={onClose} size="small">
            <Icon>close</Icon>
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{recipe.name}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={recipe.category}
                color="primary"
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                Last updated: {dayjs(recipe.updated_at).format("MMM D, YYYY")}
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, height: "calc(100% - 64px)", overflow: "auto" }}>
          <Stack spacing={3}>
            {/* Basic Info */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Overview
              </Typography>
              <Stack spacing={2}>
                {recipe.description && (
                  <Typography color="text.secondary">
                    {recipe.description}
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Chip
                    icon={<Icon>timer</Icon>}
                    label={`Prep: ${recipe.total_preparing_time} min`}
                    variant="outlined"
                  />
                  <Chip
                    icon={<Icon>microwave</Icon>}
                    label={`Cook: ${recipe.total_cooking_time} min`}
                    variant="outlined"
                  />
                  <Chip
                    icon={<Icon>people</Icon>}
                    label={`Serves: ${recipe.servings}`}
                    variant="outlined"
                  />
                  <Chip
                    icon={<Icon>payments</Icon>}
                    label={`${totalCost?.toFixed(3)} BHD`}
                    variant="outlined"
                    color="primary"
                  />
                </Box>
                {recipe.tags.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tags:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        mt: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      {recipe.tags.map((tag: string, index: number) => (
                        <Chip key={index} label={tag} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Cost History */}
            {latestCostHistory && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Cost Breakdown
                </Typography>
                <List dense>
                  {latestCostHistory.ingredient_costs.map(
                    (cost: IngredientCost, index: number) => {
                      const { name: ingredientName, parentUnit } =
                        getIngredientDetails(cost.item_id);
                      const { standardRate, standardUnit } =
                        getStandardizedRate(cost, parentUnit);

                      // Get original quantity in parent unit if available
                      const originalQuantity = parentUnit
                        ? convertUnits(cost.quantity, cost.unit, parentUnit)
                        : null;

                      return (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Icon color="primary">
                              {cost.item_type === "ingredient"
                                ? "restaurant"
                                : "inventory_2"}
                            </Icon>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="body2">
                                  {formatUnitValue(cost.quantity, cost.unit)}
                                  {originalQuantity && parentUnit && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ ml: 0.5 }}
                                    >
                                      (
                                      {formatUnitValue(
                                        originalQuantity,
                                        parentUnit
                                      )}
                                      )
                                    </Typography>
                                  )}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {ingredientName}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Rate: {standardRate.toFixed(10)} BHD per{" "}
                                  {standardUnit}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Total Cost: {cost.total_cost.toFixed(3)} BHD
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItem>
                      );
                    }
                  )}
                  <Divider sx={{ my: 1 }} />
                  <ListItem>
                    <ListItemIcon>
                      <Icon color="primary">calculate</Icon>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" color="primary">
                          Total Recipe Cost:{" "}
                          {latestCostHistory.total_cost.toFixed(3)} BHD
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Last calculated:{" "}
                          {dayjs(latestCostHistory.timestamp).format(
                            "MMM D, YYYY"
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Paper>
            )}

            {/* Ingredients */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Ingredients
              </Typography>
              <List dense>
                {recipe.ingredients.map(
                  (ingredient: RecipeIngredient, index: number) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Icon color="primary">
                          {ingredient.item_type === "ingredient"
                            ? "restaurant"
                            : "inventory_2"}
                        </Icon>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography>
                              {ingredient.quantity} {ingredient.unit}
                            </Typography>
                            {ingredient.selected_batch_id && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Batch: ${ingredient.selected_batch_id}`}
                              />
                            )}
                          </Box>
                        }
                        secondary={ingredient.notes}
                      />
                    </ListItem>
                  )
                )}
              </List>
            </Paper>

            {/* Instructions */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Instructions
              </Typography>
              <List dense>
                {recipe.instructions.map(
                  (instruction: APIInstruction, index: number) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Chip size="small" label={index + 1} color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={instruction.content}
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            {instruction.preparation_time && (
                              <Chip
                                size="small"
                                icon={<Icon fontSize="small">timer</Icon>}
                                label={`Prep: ${instruction.preparation_time} ${instruction.preparation_time_unit}`}
                                variant="outlined"
                              />
                            )}
                            {instruction.cooking_time && (
                              <Chip
                                size="small"
                                icon={<Icon fontSize="small">microwave</Icon>}
                                label={`Cook: ${instruction.cooking_time} ${instruction.cooking_time_unit}`}
                                variant="outlined"
                              />
                            )}
                            {instruction.temperature && (
                              <Chip
                                size="small"
                                icon={<Icon fontSize="small">thermostat</Icon>}
                                label={`${instruction.temperature}°${instruction.temperature_unit}`}
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        }
                      />
                    </ListItem>
                  )
                )}
              </List>
            </Paper>

            {/* Additional Info */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Additional Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Icon color="primary">event</Icon>
                  </ListItemIcon>
                  <ListItemText
                    primary="Created"
                    secondary={dayjs(recipe.created_at).format("MMM D, YYYY")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Icon color="primary">update</Icon>
                  </ListItemIcon>
                  <ListItemText
                    primary="Last Updated"
                    secondary={dayjs(recipe.updated_at).format("MMM D, YYYY")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Icon color={recipe.is_active ? "success" : "error"}>
                      {recipe.is_active ? "check_circle" : "cancel"}
                    </Icon>
                  </ListItemIcon>
                  <ListItemText
                    primary="Status"
                    secondary={recipe.is_active ? "Active" : "Inactive"}
                  />
                </ListItem>
              </List>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};
